import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Building2, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase, 
  Award, 
  Clock, 
  Car, 
  Shield,
  CheckCircle2,
  AlertCircle,
  ArrowLeft
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicJobApplicationPage() {
  const { agencyCode } = useParams();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    position_applied: "",
    experience_years: 0,
    certifications: [],
    availability: "",
    start_date: "",
    has_drivers_license: false,
    has_reliable_transportation: false,
    can_pass_background_check: false,
    references: "",
    additional_info: ""
  });

  useEffect(() => {
    fetchAgencyInfo();
  }, [agencyCode]);

  const fetchAgencyInfo = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/agency/${agencyCode}`);
      setAgency(response.data);
      setError(null);
    } catch (err) {
      setError("Agency not found. Please check the link and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCertificationToggle = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.position_applied) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/agency/${agencyCode}/job-application`, formData);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Agency Not Found</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Go to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle2 className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Application Submitted!</h1>
          <p className="text-slate-600 mb-6">
            Thank you for applying to {agency?.name}. We have received your application and will review it shortly. 
            We will contact you if your qualifications match our needs.
          </p>
          <p className="text-sm text-slate-500">
            You can close this window now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{agency?.name}</h1>
              <p className="text-slate-500">Join Our Caregiving Team</p>
            </div>
          </div>
          <div className="text-sm text-slate-600 flex flex-wrap gap-4">
            {agency?.address && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {agency.city}, {agency.state}
              </span>
            )}
            {agency?.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {agency.phone}
              </span>
            )}
            {agency?.email && (
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {agency.email}
              </span>
            )}
          </div>
        </div>

        {/* Application Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 space-y-6">
          <div className="border-b border-slate-200 pb-4">
            <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Employment Application
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Fields marked with * are required
            </p>
          </div>

          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder="John"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder="Doe"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="john@example.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder="123 Main Street"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  placeholder="Milwaukee"
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                  placeholder="WI"
                />
              </div>
              <div className="space-y-2">
                <Label>ZIP Code</Label>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => handleChange("zip_code", e.target.value)}
                  placeholder="53201"
                />
              </div>
            </div>
          </div>

          {/* Position Information */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-slate-400" />
              Position Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Position Applied For *</Label>
                <Select 
                  value={formData.position_applied} 
                  onValueChange={(v) => handleChange("position_applied", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pcw">Personal Care Worker (PCW)</SelectItem>
                    <SelectItem value="cna">Certified Nursing Assistant (CNA)</SelectItem>
                    <SelectItem value="hha">Home Health Aide (HHA)</SelectItem>
                    <SelectItem value="rn">Registered Nurse (RN)</SelectItem>
                    <SelectItem value="lpn">Licensed Practical Nurse (LPN)</SelectItem>
                    <SelectItem value="case_manager">Case Manager</SelectItem>
                    <SelectItem value="hr_scheduler">HR/Scheduler</SelectItem>
                    <SelectItem value="evv_specialist">EVV Specialist</SelectItem>
                    <SelectItem value="receptionist">Receptionist</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Years of Experience</Label>
                <Select 
                  value={String(formData.experience_years)} 
                  onValueChange={(v) => handleChange("experience_years", parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select experience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">No experience</SelectItem>
                    <SelectItem value="1">Less than 1 year</SelectItem>
                    <SelectItem value="2">1-2 years</SelectItem>
                    <SelectItem value="3">3-5 years</SelectItem>
                    <SelectItem value="5">5-10 years</SelectItem>
                    <SelectItem value="10">10+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Availability</Label>
                <Select 
                  value={formData.availability} 
                  onValueChange={(v) => handleChange("availability", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select availability" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full-Time</SelectItem>
                    <SelectItem value="part_time">Part-Time</SelectItem>
                    <SelectItem value="per_diem">Per Diem / PRN</SelectItem>
                    <SelectItem value="weekends">Weekends Only</SelectItem>
                    <SelectItem value="flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Earliest Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange("start_date", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Certifications */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Award className="w-4 h-4 text-slate-400" />
              Certifications (Check all that apply)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                "CNA - Certified Nursing Assistant",
                "HHA - Home Health Aide",
                "CPR/First Aid",
                "Medication Administration",
                "RN License",
                "LPN License",
                "Dementia Care",
                "Hospice Care"
              ].map((cert) => (
                <div key={cert} className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.certifications.includes(cert)}
                    onCheckedChange={() => handleCertificationToggle(cert)}
                    id={cert}
                  />
                  <label htmlFor={cert} className="text-sm text-slate-700 cursor-pointer">
                    {cert}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Requirements */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Shield className="w-4 h-4 text-slate-400" />
              Requirements
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.has_drivers_license}
                  onCheckedChange={(c) => handleChange("has_drivers_license", c)}
                  id="drivers_license"
                />
                <label htmlFor="drivers_license" className="flex items-center gap-2 cursor-pointer">
                  <Car className="w-4 h-4 text-slate-400" />
                  <span className="text-sm">I have a valid driver's license</span>
                </label>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.has_reliable_transportation}
                  onCheckedChange={(c) => handleChange("has_reliable_transportation", c)}
                  id="transportation"
                />
                <label htmlFor="transportation" className="flex items-center gap-2 cursor-pointer">
                  <Car className="w-4 h-4 text-slate-400" />
                  <span className="text-sm">I have reliable transportation</span>
                </label>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={formData.can_pass_background_check}
                  onCheckedChange={(c) => handleChange("can_pass_background_check", c)}
                  id="background"
                />
                <label htmlFor="background" className="flex items-center gap-2 cursor-pointer">
                  <Shield className="w-4 h-4 text-slate-400" />
                  <span className="text-sm">I can pass a background check</span>
                </label>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-slate-700">Additional Information</h3>
            <div className="space-y-2">
              <Label>References</Label>
              <Textarea
                value={formData.references}
                onChange={(e) => handleChange("references", e.target.value)}
                placeholder="Please provide 2-3 professional references with contact information"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Tell us about yourself</Label>
              <Textarea
                value={formData.additional_info}
                onChange={(e) => handleChange("additional_info", e.target.value)}
                placeholder="Why are you interested in caregiving? Any additional information you'd like to share?"
                rows={3}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="pt-6 border-t">
            <Button 
              type="submit" 
              className="w-full py-6 text-lg gap-2"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  Submit Application
                </>
              )}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-4">
              By submitting this application, you authorize {agency?.name} to contact you regarding employment opportunities.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
